import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { formatNOK } from "@/lib/format";
import { calcLiveBfEstimated, isSummaryLine, isTravelLine, isUpsellLine } from "@/lib/billing-calculations";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { SrIcon } from "@/components/ui/SrIcon";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCreateBudgetLine } from "@/hooks/use-budget-lines";
import { OfferLineEditRow } from "@/components/project/OfferLineEditRow";
import {
  OfferMetadataEditCard,
  AddBudgetLineButton,
} from "@/components/project/OfferMetadataEditCard";
import { computeBudgetLineActualCost } from "@/lib/budget-line-actual-cost";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useBudgetRevisionGuards } from "@/hooks/use-budget-revision-guards";
import { BudgetRevisionMode } from "@/components/project/budget-revision/BudgetRevisionMode";

/**
 * VIB-579/VIB-586: Økonomi → Tilbud
 *
 * Read-only oversikt over budsjettlinjer for vanlige brukere; superadmin
 * kan toggle "Rediger" for inline-redigering av linjer + overhead/rabatt.
 * Endringer skrives til budget_lines + project_offer_metadata, samme rader
 * som Timetildeling-fanen leser fra — så endringer plukkes opp begge
 * steder via React Query cache invalidation.
 */
export default function OkonomiTilbudPage() {
  const {
    project,
    budgetLines,
    offerMetadata,
    permissions,
    voucherAllocations,
    timeEntries,
    isAdmin,
    isProductionLead,
  } = useProjectDetailContext();
  // VIB-722c: view_economics gates tilgang til hele tilbuds-siden.
  // edit_contract_amount gates redigeringsmodus (toggle, lagre, legg til linje).
  // Admin/superadmin overstyrer begge via system-rolle.
  const canEdit = permissions.edit_contract_amount;
  const [editing, setEditing] = useState(false);
  const [revising, setRevising] = useState(false);
  const createMut = useCreateBudgetLine();

  // VIB-1202: revisjonsmodus bak feature_budget_revision (default off).
  // Rett = PL på prosjektet + admin (Erik 15.07), kun på aktive prosjekter
  // (completed blokkeres uansett av assert_project_writable).
  const revisionFlagOn = useFeatureFlag("feature_budget_revision");
  const canRevise =
    revisionFlagOn && (isAdmin || isProductionLead) && project.project_status === "active";
  const { data: revisionGuards } = useBudgetRevisionGuards(
    canRevise ? project.id : undefined,
    budgetLines,
    timeEntries,
  );

  if (!permissions.view_economics) {
    return <Navigate to={`/prosjekter/${project.id}`} replace />;
  }

  if (revising) {
    return (
      <ErrorBoundary>
        <BudgetRevisionMode
          project={project}
          budgetLines={budgetLines}
          offerMetadata={offerMetadata}
          guards={revisionGuards ?? new Map()}
          onExit={() => setRevising(false)}
        />
      </ErrorBoundary>
    );
  }

  // Filtrer ut mersalg-linjer (kobles via upsell_id; vises i egen Mersalg-fane).
  // Legacy-fallback: noen prosjekter har phase="mersalg" fra gammel import.
  const nonMersalg = budgetLines.filter(
    (l) => !isUpsellLine(l) && l.phase !== "mersalg",
  );
  // Reise-linjer vises i egen seksjon under fase-tabellen (herav),
  // men inngår i overhead-grunnlaget (VIB-2086).
  const regularLines = nonMersalg.filter(
    (l) => !isSummaryLine(l) && !isTravelLine(l),
  );
  const travelLines = nonMersalg.filter(isTravelLine);
  const summaryLines = nonMersalg.filter((l) => isSummaryLine(l));

  // VIB-1295: Påløpt kostnad per linje (kun approved-bilag, se lib-filen for
  // avgrensning). Beregnes over alle ikke-mersalg-linjer (regular + reise);
  // summary-linjer har ingen bilag-allokeringer i praksis.
  const actualCostByLine = computeBudgetLineActualCost(nonMersalg, voucherAllocations);
  const actualCostTotal = [...regularLines, ...travelLines].reduce(
    (s, l) => s + (actualCostByLine.get(l.id) ?? 0),
    0,
  );
  const travelActualCost = travelLines.reduce(
    (s, l) => s + (actualCostByLine.get(l.id) ?? 0),
    0,
  );

  const bfCalc = calcLiveBfEstimated(nonMersalg, offerMetadata);
  const {
    regularTotal: budgetTotal,
    overheadAmt: overheadAmount,
    discountAmt: discountAmount,
    travelTotal,
    liveTotalAmount: calculatedTotal,
    estExtCostTotal: totalExtCost,
    liveBfEstimated: estBf,
    liveBfEstimatedPct: estBfPct,
  } = bfCalc;

  const handleAddLine = (phase: ProjectPhase) => {
    createMut.mutate({
      project_id: project.id,
      phase,
      description: "Ny linje",
      amount: 0,
      cost_type: "internal",
      quantity: null,
      unit: "t",
      quantity_hours: 0,
      estimated_cost: 0,
    });
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="border-[1.5px] border-foreground bg-card">
          <div className="px-4 py-3 border-b-[1.5px] border-foreground flex items-center gap-2">
            <SrIcon name="receipt" className="w-4 h-4 text-muted-foreground" />
            <span className="font-display font-semibold text-lg tracking-tight">Tilbud</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {editing
                ? "Endringer lagres automatisk"
                : canEdit
                  ? "Klikk Rediger for å endre linjer"
                  : "Read-only kopi av tilbudslinjene"}
            </span>
            {canRevise && !editing && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs ml-2 border-amber-500 text-amber-800 hover:bg-amber-50"
                onClick={() => setRevising(true)}
              >
                <SrIcon name="edit" className="w-3 h-3 mr-1" />
                Revider budsjett
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                variant={editing ? "default" : "outline"}
                className="h-7 text-xs ml-2"
                onClick={() => setEditing((e) => !e)}
              >
                <SrIcon name={editing ? "check" : "edit"} className="w-3 h-3 mr-1" />
                {editing ? "Ferdig" : "Rediger"}
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-muted-foreground uppercase tracking-wider border-b text-[11px]">
                  <th className="text-left font-medium px-4 py-2.5">Linje</th>
                  <th className="text-right font-medium px-3 py-2.5 w-20">{editing ? "Antall" : "Timer"}</th>
                  <th className="text-right font-medium px-3 py-2.5 w-32">Beløp</th>
                  <th className="text-right font-medium px-4 py-2.5 w-32">
                    {editing ? "Est. kost / handling" : "Est. kostnad"}
                  </th>
                  <th className="text-right font-medium px-4 py-2.5 w-32">Påløpt</th>
                </tr>
              </thead>
              <tbody>
                {(Object.keys(PHASE_LABELS) as ProjectPhase[])
                  .filter((phase) => phase !== "reise")
                  .map((phase) => {
                  const lines = regularLines.filter((bl) => bl.phase === phase);
                  if (lines.length === 0 && !editing) return null;
                  const phaseTotal = lines.reduce((s, l) => s + (l.amount ?? 0), 0);
                  const phaseHours = lines
                    .filter((l) => l.cost_type === "internal")
                    .reduce((s, l) => s + (l.quantity_hours ?? 0), 0);
                  const phaseExtCost = lines
                    .filter((l) => l.cost_type === "external")
                    .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
                  const phaseActualCost = lines.reduce(
                    (s, l) => s + (actualCostByLine.get(l.id) ?? 0),
                    0,
                  );
                  return (
                    <React.Fragment key={phase}>
                      <tr className="bg-muted/30">
                        <td className="px-4 py-2 font-medium">{PHASE_LABELS[phase]}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {phaseHours}t
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {formatNOK(phaseTotal)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {phaseExtCost > 0 ? (
                            formatNOK(phaseExtCost)
                          ) : (
                            <span className="text-muted-foreground font-normal">—</span>
                          )}
                        </td>
                        <td
                          className={cn(
                            "px-4 py-2 text-right tabular-nums font-medium",
                            phaseActualCost > phaseExtCost && "text-destructive",
                          )}
                        >
                          {phaseActualCost > 0 ? (
                            formatNOK(phaseActualCost)
                          ) : (
                            <span className="text-muted-foreground font-normal">—</span>
                          )}
                        </td>
                      </tr>
                      {lines.map((bl) => (
                        <OfferLineEditRow
                          key={bl.id}
                          bl={bl}
                          projectId={project.id}
                          editing={editing}
                          actualCost={actualCostByLine.get(bl.id)}
                        />
                      ))}
                      {editing && (
                        <AddBudgetLineButton
                          onAdd={() => handleAddLine(phase)}
                          disabled={createMut.isPending}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {(travelLines.length > 0 || editing) && (
                  <React.Fragment>
                    <tr className="bg-muted/30">
                      <td className="px-4 py-2 font-medium">
                        <div className="flex items-center gap-2">
                          {PHASE_LABELS.reise}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {travelLines
                          .filter((l) => l.cost_type === "internal")
                          .reduce((s, l) => s + (l.quantity_hours ?? 0), 0)}t
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatNOK(travelTotal)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {(() => {
                          const ext = travelLines
                            .filter((l) => l.cost_type === "external")
                            .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
                          return ext > 0 ? formatNOK(ext) : (
                            <span className="text-muted-foreground font-normal">—</span>
                          );
                        })()}
                      </td>
                      <td
                        className={cn(
                          "px-4 py-2 text-right tabular-nums font-medium",
                          (() => {
                            const ext = travelLines
                              .filter((l) => l.cost_type === "external")
                              .reduce((s, l) => s + (l.estimated_cost ?? 0), 0);
                            return travelActualCost > ext && "text-destructive";
                          })(),
                        )}
                      >
                        {travelActualCost > 0 ? (
                          formatNOK(travelActualCost)
                        ) : (
                          <span className="text-muted-foreground font-normal">—</span>
                        )}
                      </td>
                    </tr>
                    {travelLines.map((bl) => (
                      <OfferLineEditRow
                        key={bl.id}
                        bl={bl}
                        projectId={project.id}
                        editing={editing}
                        actualCost={actualCostByLine.get(bl.id)}
                      />
                    ))}
                    {editing && (
                      <AddBudgetLineButton
                        onAdd={() => handleAddLine("reise")}
                        disabled={createMut.isPending}
                      />
                    )}
                  </React.Fragment>
                )}

                {summaryLines.length > 0 && (
                  <React.Fragment>
                    <tr className="bg-muted/30">
                      <td className="px-4 py-2 font-medium">Tilbud-justeringer</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">—</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatNOK(summaryLines.reduce((s, l) => s + (l.amount ?? 0), 0))}
                      </td>
                      <td className="px-4 py-2 text-right text-muted-foreground">—</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">—</td>
                    </tr>
                    {summaryLines.map((bl) => (
                      <tr key={bl.id} className="border-b border-border/40">
                        <td className="px-4 py-2 pl-8">{bl.description}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">—</td>
                        <td
                          className={cn(
                            "px-3 py-2 text-right tabular-nums",
                            (bl.amount ?? 0) < 0 && "text-destructive",
                          )}
                        >
                          {formatNOK(bl.amount ?? 0)}
                        </td>
                        <td className="px-4 py-2 text-right text-muted-foreground">—</td>
                        <td className="px-4 py-2 text-right text-muted-foreground">—</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[1.5px] bg-foreground border-[1.5px] border-foreground">
          <OfferMetadataEditCard
            projectId={project.id}
            metadata={offerMetadata}
            budgetTotal={budgetTotal}
            overheadAmount={overheadAmount}
            discountAmount={discountAmount}
            travelTotal={travelTotal}
            calculatedTotal={calculatedTotal}
            editing={editing}
          />

          <div className="bg-card p-5 pb-[22px]">
            <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">
              Estimert GEEK
            </p>
            <p
              className={cn(
                "font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2",
                estBf >= 0 ? "text-success" : "text-destructive",
              )}
            >
              {formatNOK(estBf)}
              <span className="text-[18px] opacity-70 ml-2">({estBfPct}%)</span>
            </p>
            <div className="mt-3 space-y-1 text-[12.5px] text-muted-foreground">
              <div className="flex justify-between">
                <span>Tilbudssum</span>
                <span className="tabular-nums">{formatNOK(calculatedTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>− Eksterne kostnader (estimert)</span>
                <span className="tabular-nums">{formatNOK(totalExtCost)}</span>
              </div>
              <div className="flex justify-between">
                <span>Påløpt kostnad (godkjente bilag)</span>
                <span
                  className={cn(
                    "tabular-nums",
                    actualCostTotal > totalExtCost && "text-destructive font-medium",
                  )}
                >
                  {actualCostTotal > 0 ? formatNOK(actualCostTotal) : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
